import { useState } from 'react';
import cn from 'classnames';
import { SVG_SIZE_L } from 'src/constants';
import { Button } from 'src/v2/commonComponents/Button';
import {
  useCustomNinjaModelName,
  useFeedbackRating,
  useVisible,
} from 'src/hooks';
import { Icon, IconName } from 'src/components/Icon';
import { Modal } from 'src/components/Modal';
import styles from './FeedbackRating.module.scss';
import { TextareaSimple } from 'src/v2/commonComponents/TextareaSimple';
import { FeedbackOperationType, Message, MessageSkill } from 'src/types';
import { useChatHistory } from 'src/hooks';

const FEEDBACK_MAP: { rating: number; title: string; icon: IconName }[] = [
  {
    rating: 1,
    title: 'Incorrect',
    icon: 'sentiment-very-dissatisfied',
  },
  {
    rating: 2,
    title: 'Poor',
    icon: 'sentiment-dissatisfied',
  },
  {
    rating: 3,
    title: 'Fair',
    icon: 'sentiment-neutral',
  },
  {
    rating: 4,
    title: 'Good',
    icon: 'sentiment-satisfied',
  },
  {
    rating: 5,
    title: 'Superb',
    icon: 'sentiment-very-satisfied',
  },
];

interface FeedbackRatingProps {
  message: Message;
  title?: string;
  tabTitle?: string;
  isStreaming: boolean;
  operationType: FeedbackOperationType;
  modelId?: string;
  messageSkill: MessageSkill | null;
}

export const FeedbackRating = ({
  message,
  tabTitle,
  isStreaming,
  title = 'Rate the response',
  operationType,
  modelId,
  messageSkill,
}: FeedbackRatingProps) => {
  const { isNinjaRetired } = useChatHistory();
  const { currentRating, updateFeedbackRating, addFeedbackDetails, isLoading } =
    useFeedbackRating({
      message,
      modelId,
      operationType,
    });

  const { customModelName } = useCustomNinjaModelName({
    modelIds: message.model_ids,
    messageSkill,
  });

  const { isVisible, onVisibilityRemove, onVisibilitySet } = useVisible();
  const [value, setValue] = useState<string>('');

  if (isStreaming) {
    return null;
  }

  const currentTitle = tabTitle
    ? `Rate ${customModelName || tabTitle} answer`
    : title;

  const handleSubmit = async () => {
    await addFeedbackDetails(value);
    onVisibilityRemove();
    setValue('');
  };

  if (isNinjaRetired) {
    return null;
  }

  return (
    <>
      <div className={styles.root}>
        <div className={styles.content}>
          <p className={styles.title}>{currentTitle}</p>

          <Button
            onClick={onVisibilitySet}
            disabled={currentRating === undefined}
            color="link"
            data-e2e="task-rating-text-button"
          >
            Tell us why?
          </Button>
        </div>

        <div className={styles.ratingWrapper}>
          {FEEDBACK_MAP.map(({ rating, icon, title }) => {
            const isSelected = currentRating === rating;

            return (
              <Button
                className={cn(styles.ratingButton, {
                  [styles.selected]: isSelected,
                })}
                key={rating}
                color="transparent"
                onClick={() =>
                  updateFeedbackRating({ rating, operationType, modelId })
                }
                data-e2e={`task-rating-${rating}`}
              >
                <Icon
                  weight={isSelected ? 'fill' : 'regular'}
                  type={icon}
                  size={SVG_SIZE_L}
                />
                <span className={styles.iconTitle}>{title}</span>
              </Button>
            );
          })}
        </div>
      </div>

      {isVisible && (
        <Modal
          open={isVisible}
          onClose={onVisibilityRemove}
          blockScroll={false}
        >
          <div className={styles.modalContainer}>
            <p className={styles.modalTitle}>Tell us why?</p>

            <TextareaSimple
              minRows={3}
              maxRows={3}
              value={value}
              placeholder="Write the feedback"
              onChange={setValue}
              data-e2e="task-rating-text"
            />

            <Button
              onClick={handleSubmit}
              className={styles.submitButton}
              disabled={isLoading}
              data-e2e="task-rating-text-submit"
            >
              Submit
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};
